import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, ShieldCheck, Users, Sparkles } from "lucide-react";

const emptyAdult = () => ({
  salutation: "Mr", first_name: "", last_name: "", identity_document: "",
  passport_country: "", telephone: "", email: "", residential_address: "", date_of_birth: "",
});
const emptyChild = () => ({
  gender: "Boy", first_name: "", last_name: "", date_of_birth: "",
  guardian_first_name: "", guardian_last_name: "", guardian_identity_document: "",
});
const money = (value) => value == null ? "—" : new Intl.NumberFormat("en-HK", {
  style: "currency", currency: "HKD", maximumFractionDigits: 0,
}).format(value);
const isFamily = (plan) => /family/i.test(plan || "");
const isIndividual = (plan) => /individual/i.test(plan || "") && !isFamily(plan);
const isSingleTrip = (plan) => /single/i.test(plan || "");

function Field({
    label,
    value,
    onChange,
    type = "text",
    required = true,
    error,
    ...props
}) {
    return (
        <div className="space-y-1.5">
            <Label>{label}{required && " *"}</Label>
            <Input
                type={type}
                value={value ?? ""}
                required={required}
                aria-invalid={Boolean(error)}
                onChange={(e) => onChange(e.target.value)}
                {...props}
            />
            {error && (
                <p className="text-xs text-red-600">{error}</p>
            )}
        </div>
    );
}

function SelectField({
    label,
    value,
    onChange,
    options,
    required = true,
    error,
}) {
    return (
        <div className="space-y-1.5">
            <Label>{label}{required && " *"}</Label>
            <select
                value={value ?? ""}
                required={required}
                aria-invalid={Boolean(error)}
                onChange={(e) => onChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
                <option value="">Select…</option>
                {options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                ))}
            </select>
            {error && (
                <p className="text-xs text-red-600">{error}</p>
            )}
        </div>
    );
}

const TRAVEL_PLAN_DOCS = [
    {
        carriers: ["MSIG"],
        planType: "single",
        title: "MSIG Travel Insurance",
        file: "/pdf/msig_single_trip_travel.pdf",
        caption: "PDF · Policy and benefit details",
    },
    {
        carriers: ["ZURICH"],
        planType: "single",
        title: "Zurich Travel Insurance",
        file: "/pdf/Zurich_GETZGOPLUS_FS_EN_brochure.pdf",
        caption: "PDF · Cover and benefits",
    },
    {
        carriers: ["MSIG"],
        planType: "annual",
        title: "MSIG Travel Insurance",
        file: "/pdf/msig_single_trip_travel.pdf",
        caption: "PDF · Policy and benefit details",
    },
    {
        carriers: ["ZURICH"],
        planType: "annual",
        title: "Zurich Travel Insurance",
        file: "/pdf/Zurich_GETZGOPLUS_FS_EN_brochure.pdf",
        caption: "PDF · Cover and benefits",
    },
];

const getTravelPlanDocs = (travelPlan, carrier) => {
    const plan = (travelPlan || "").trim().toLowerCase();
    const provider = (carrier || "").trim().toLowerCase();

    const planType = plan.includes("annual")
        ? "annual"
        : plan.includes("single")
            ? "single"
            : null;

    return TRAVEL_PLAN_DOCS.filter((doc) => {
        const carrierMatches = doc.carriers.some(
            (name) => name.toLowerCase() === provider
        );

        return carrierMatches && doc.planType === planType;
    });
};


export default function TravelInsurance() {
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get("dealId") || searchParams.get("deal_id") || null;
  const [meta, setMeta] = useState(null);
  const [travelPlan, setTravelPlan] = useState("");
  const [adultsCount, setAdultsCount] = useState("1");
  const [childrenCount, setChildrenCount] = useState("0");
  const [tripDays, setTripDays] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quote, setQuote] = useState(null);
  const [adults, setAdults] = useState([emptyAdult()]);
  const [children, setChildren] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const [understand, setUnderstand] = useState(null); // null | "yes" | "no"
    const [errors, setErrors] = useState({});

  const family = isFamily(travelPlan);
  const individual = isIndividual(travelPlan);
  const singleTrip = isSingleTrip(travelPlan);

  useEffect(() => {
      api.get("/member/hk-travel-insurance/options")
      .then((r) => setMeta(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    setOptions([]);
    setSelected(null);
    setQuote(null);
    setSubmitted(null);
  }, [travelPlan, adultsCount, childrenCount, tripDays]);

  useEffect(() => {
    const adultTarget = family ? Number(adultsCount || 1) : 1;
    const childTarget = family ? Number(childrenCount || 0) : 0;
    setAdults((old) => Array.from({ length: adultTarget }, (_, i) => old[i] || emptyAdult()));
    setChildren((old) => Array.from({ length: childTarget }, (_, i) => old[i] || emptyChild()));
  }, [family, adultsCount, childrenCount]);

  const canLoadOptions = Boolean(travelPlan) &&
    (!family || (adultsCount !== "" && childrenCount !== "")) &&
    (!singleTrip || (Number(tripDays) >= 1 && Number(tripDays) <= 366));

  const loadOptions = async () => {
    if (!canLoadOptions) return;
    setLoadingOptions(true);
    try {
        const r = await api.post("/member/hk-travel-insurance/options-for-plan", {
        travel_plan: travelPlan,
        adults: family ? Number(adultsCount) : (individual ? 1 : null),
        children: family ? Number(childrenCount) : (individual ? 0 : null),
        trip_days: singleTrip ? Number(tripDays) : null,
      });
      setOptions(r.data.options || []);
      if (!r.data.options?.length) toast.message("No matching provider options for these selections.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingOptions(false);
    }
  };

  const chooseOption = async (option) => {
    setSelected(option);
    setQuote(null);
    setLoadingPrice(true);
    try {
        const r = await api.post("/member/hk-travel-insurance/price", {
        travel_plan: travelPlan,
        adults: family ? Number(adultsCount) : 1,
        children: family ? Number(childrenCount) : 0,
        trip_days: singleTrip ? Number(tripDays) : null,
        carrier: option.carrier,
        benefit_tier: option.benefit_tier,
      });
      setQuote(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setSelected(null);
    } finally {
      setLoadingPrice(false);
    }
  };

    const filteredTravelDocs = useMemo(
        () => getTravelPlanDocs(travelPlan, selected?.carrier),
        [travelPlan, selected?.carrier]
    );

  const updateAdult = (index, key, value) =>
    setAdults((old) => old.map((p, i) => i === index ? { ...p, [key]: value } : p));
  const updateChild = (index, key, value) =>
    setChildren((old) => old.map((p, i) => i === index ? { ...p, [key]: value } : p));

    const validateForm = () => {
        const next = {};

        // Applicant declaration: the applicant must agree to proceed.
        if (understand !== "yes") {
            next.understand =
                understand === "no"
                    ? "You must agree to the declaration to submit this application."
                    : "Please confirm that you understand the applicant declaration.";
        }

        // Validate every insured adult.
        adults.forEach((person, i) => {
            const prefix = `adult_${i}`;

            [
                ["salutation", "Salutation"],
                ["date_of_birth", "Date of birth"],
                ["first_name", "First name"],
                ["last_name", "Last name"],
                ["identity_document", "HKID or passport number"],
                ["passport_country", "Country of passport"],
                ["telephone", "Telephone"],
                ["email", "Email"],
                ["residential_address", "Residential address"],
            ].forEach(([key, label]) => {
                if (!String(person[key] ?? "").trim()) {
                    next[`${prefix}_${key}`] = `${label} is required.`;
                }
            });

            if (
                person.email &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email.trim())
            ) {
                next[`${prefix}_email`] = "Enter a valid email address.";
            }

            if (
                person.date_of_birth &&
                (
                    Number.isNaN(Date.parse(person.date_of_birth)) ||
                    person.date_of_birth > new Date().toISOString().slice(0, 10)
                )
            ) {
                next[`${prefix}_date_of_birth`] =
                    "Enter a valid date of birth that is not in the future.";
            }
        });

        // Validate every insured child and their guardian details.
        children.forEach((child, i) => {
            const prefix = `child_${i}`;

            [
                ["gender", "Gender"],
                ["date_of_birth", "Date of birth"],
                ["first_name", "First name"],
                ["last_name", "Last name"],
                ["guardian_first_name", "Guardian first name"],
                ["guardian_last_name", "Guardian surname"],
                ["guardian_identity_document", "Guardian HKID or passport number"],
            ].forEach(([key, label]) => {
                if (!String(child[key] ?? "").trim()) {
                    next[`${prefix}_${key}`] = `${label} is required.`;
                }
            });

            if (
                child.date_of_birth &&
                (
                    Number.isNaN(Date.parse(child.date_of_birth)) ||
                    child.date_of_birth > new Date().toISOString().slice(0, 10)
                )
            ) {
                next[`${prefix}_date_of_birth`] =
                    "Enter a valid date of birth that is not in the future.";
            }
        });

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please correct the highlighted fields before submitting.");
            return;
        }

        if (!selected || !quote?.available) {
            toast.error("Select a provider and benefit tier first.");
            return;
        }
    setSubmitting(true);
    try {
        const r = await api.post("/member/hk-travel-insurance/submit", {
        travel_plan: travelPlan,
        adults_count: family ? Number(adultsCount) : 1,
        children_count: family ? Number(childrenCount) : 0,
        trip_days: singleTrip ? Number(tripDays) : null,
        carrier: selected.carrier,
        benefit_tier: selected.benefit_tier,
        adults,
        children,
        deal_id: dealId,
      });
      setSubmitted(r.data);
      toast.success(`Travel insurance enquiry submitted (${r.data.display_id}).`);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta) return <div className="p-8 text-sm text-muted-foreground">Loading travel insurance options…</div>;

  if (submitted) return <div className="mx-auto max-w-3xl p-6">
    <Card><CardContent className="p-8 text-center space-y-4">
      <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
      <h1 className="text-2xl font-semibold">Application submitted</h1>
      <p>Your reference is <strong>{submitted.display_id}</strong>.</p>
      <p className="text-sm text-muted-foreground">
        {submitted.refer_for_quote ? "The provider needs to confirm your premium." : "Your selected premium has been saved with your application."}
      </p>
      <Button onClick={() => window.history.back()} variant="outline">Return</Button>
    </CardContent></Card>
  </div>;

  return <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-8">
    <div className="space-y-2">
      <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">Perx Insurance</p>
      <h1 className="text-3xl font-bold tracking-tight">Travel Insurance</h1>
      <p className="text-muted-foreground">Compare available providers and cover options, then submit traveller details for your selected policy.</p>
    </div>

    <Card>
      <CardHeader><CardTitle>1. Define your travel plan</CardTitle>
        <CardDescription>Choose the travel plan first. Available providers and benefits will be filtered to your selections.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <SelectField label="Travel plan" value={travelPlan} onChange={setTravelPlan} options={meta.travel_plans || []} />
        {family && <>
          <SelectField label="Number of adults" value={adultsCount} onChange={setAdultsCount} options={["1", "2"]} />
          <SelectField label="Number of children" value={childrenCount} onChange={setChildrenCount} options={["0", "1", "2", "3", "4"]} />
        </>}
        {singleTrip && <Field label="Trip duration (days)" type="number" min="1" max="366"
          value={tripDays} onChange={setTripDays} placeholder="e.g. 14" />}
        {travelPlan && <div className="md:col-span-2 flex justify-end">
          <Button type="button" onClick={loadOptions} disabled={!canLoadOptions || loadingOptions}>
            {loadingOptions && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Show provider and PDF options
          </Button>
        </div>}
      </CardContent>
    </Card>

    {options.length > 0 && <Card>
      <CardHeader><CardTitle>2. Provider and benefits options</CardTitle>
        <CardDescription>Only options matching the selected plan, family composition and trip duration are shown.</CardDescription>
          </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2">
        {options.map((option) => {
          const active = selected?.id === option.id ||
            (selected?.carrier === option.carrier && selected?.benefit_tier === option.benefit_tier);
          return <button key={`${option.carrier}-${option.benefit_tier}`} type="button"
            onClick={() => chooseOption(option)}
            className={`rounded-xl border p-4 text-left transition ${active ? "border-primary ring-2 ring-primary/20" : "hover:border-primary/50"}`}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="text-xs uppercase tracking-wide text-muted-foreground">{option.carrier}</p>
                <h3 className="mt-1 font-semibold">{option.benefit_tier}</h3></div>
              {active && <ShieldCheck className="h-5 w-5 text-primary" />}
            </div>
            <div className="mt-4">
              {option.priced ? <>
                <p className="text-sm text-muted-foreground line-through">{money(option.original)}</p>
                <p className="text-2xl font-bold">{money(option.discounted)}</p>
                <p className="text-sm text-green-700">Save {money(option.savings)}{option.discount_pct != null ? ` (${option.discount_pct}%)` : ""}</p>
              </> : <p className="text-sm font-medium">Contact provider for a quotation</p>}
            </div>
            {option.pdf_url && <p className="mt-3 text-sm"><a href={option.pdf_url} target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2 underline"><FileText className="h-4 w-4" />View plan PDF</a></p>}
          </button>;
        })}
      </CardContent>
      </Card>}

      {selected && filteredTravelDocs.length > 0 && (
          <Card data-testid="travel-insurance-docs">
              <CardHeader>
                  <CardTitle>Selected plan documents</CardTitle>
                  <CardDescription>
                      Download the policy documents for {selected.carrier}.
                  </CardDescription>
              </CardHeader>

              <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filteredTravelDocs.map((doc) => (
                          <a
                              key={doc.file}
                              href={doc.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="group flex items-start gap-3 rounded-xl border border-navy-100 bg-white p-4 transition-all hover:border-gold-500 hover:shadow-soft"
                          >
                              <FileText
                                  className="mt-0.5 shrink-0 text-gold-600"
                                  size={20}
                              />

                              <div className="min-w-0">
                                  <div className="text-sm font-semibold text-navy-700 group-hover:text-navy-900">
                                      {doc.title}
                                  </div>

                                  <div className="mt-1 text-xs text-navy-400">
                                      {doc.caption}
                                  </div>

                                  <div className="mt-2 text-xs font-medium text-navy-700">
                                      Download PDF ↓
                                  </div>
                              </div>
                          </a>
                      ))}
                  </div>
              </CardContent>
          </Card>
      )}


      {selected && <Card>
      <CardHeader><CardTitle>3. Selected premium</CardTitle></CardHeader>
      <CardContent>
        {loadingPrice ? <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Looking up the exact price…</div>
          : quote?.priced ? <div className="grid gap-4 sm:grid-cols-3">

                          <PriceCard
                              label="Original Price"
                              value={quote.original}
                              currency={quote.currency}
                              testid="helper-original"
                          />
                          <PriceCard
                              label="Discounted Price"
                              value={quote.discounted}
                              currency={quote.currency}
                              testid="helper-discounted"
                              highlight
                          />
                      <PriceCard
                          label="Savings"
                          value={quote.savings}
                              currency={quote.currency}
                              testid="helper-savings"
                              tone="emerald"
                          />
                     


                  </div> : <p className="text-sm text-muted-foreground">{quote?.contact_text || quote?.reason || "The provider must confirm the premium."}</p>}
              <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-sm text-navy-700">
                      <Sparkles className="text-gold-600 shrink-0" size={18} />
                      <div>
                          <div className="font-semibold">
                              {quote?.discount_pct != null
                                  ? `${quote.discount_pct}% off the standard premium`
                                  : "Perx member pricing applied"}
                              
                          </div>
                      </div>
                  </div>
                  {quote?.summary && <p className="mt-4 text-sm text-muted-foreground">{quote.summary}</p>}
              </div>
        
      </CardContent>
    </Card>}

    {selected && quote?.available && <form onSubmit={submit} className="space-y-6">
      <Card>
        <CardHeader><CardTitle>4. Insured-person details</CardTitle>
          <CardDescription>Complete the required details for every insured adult and child.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {adults.map((person, i) => <section key={`adult-${i}`} className="space-y-4">
            <div className="flex items-center gap-2"><Users className="h-5 w-5" /><h3 className="font-semibold">{family ? `Adult ${i + 1}` : "Individual insured person"}</h3></div>
            <div className="grid gap-4 md:grid-cols-2">
                  <SelectField
                      label="Salutation"
                      value={person.salutation}
                      onChange={(v) => updateAdult(i, "salutation", v)}
                      options={meta.salutations || ["Mr", "Mrs", "Ms", "Dr"]}
                      error={errors[`adult_${i}_salutation`]}
                  />

                  <Field
                      label="Date of birth"
                      type="date"
                      value={person.date_of_birth}
                      onChange={(v) => updateAdult(i, "date_of_birth", v)}
                      error={errors[`adult_${i}_date_of_birth`]}
                  />

                  <Field
                      label="First name"
                      value={person.first_name}
                      onChange={(v) => updateAdult(i, "first_name", v)}
                      error={errors[`adult_${i}_first_name`]}
                  />

                  <Field
                      label="Last name"
                      value={person.last_name}
                      onChange={(v) => updateAdult(i, "last_name", v)}
                      error={errors[`adult_${i}_last_name`]}
                  />

                  <Field
                      label="HKID or passport number"
                      value={person.identity_document}
                      onChange={(v) => updateAdult(i, "identity_document", v)}
                      error={errors[`adult_${i}_identity_document`]}
                  />

                  <Field
                      label="Country of passport"
                      value={person.passport_country}
                      onChange={(v) => updateAdult(i, "passport_country", v)}
                      error={errors[`adult_${i}_passport_country`]}
                  />

                  <Field
                      label="Telephone"
                      type="tel"
                      value={person.telephone}
                      onChange={(v) => updateAdult(i, "telephone", v)}
                      error={errors[`adult_${i}_telephone`]}
                  />

                  <Field
                      label="Email"
                      type="email"
                      value={person.email}
                      onChange={(v) => updateAdult(i, "email", v)}
                      error={errors[`adult_${i}_email`]}
                  />

                  <Field
                      label="Residential address"
                      value={person.residential_address}
                      onChange={(v) => updateAdult(i, "residential_address", v)}
                      error={errors[`adult_${i}_residential_address`]}
                  />
              <div className="md:col-span-2"><Field label="Residential address" value={person.residential_address} onChange={(v) => updateAdult(i, "residential_address", v)} /></div>
            </div>
          </section>)}
                  {children.map((child, i) => (
                      <section
                          key={`child-${i}`}
                          className="space-y-4 border-t pt-6"
                      >
                          <h3 className="font-semibold">Child {i + 1}</h3>

                          <div className="grid gap-4 md:grid-cols-2">
                              <SelectField
                                  label="Gender"
                                  value={child.gender}
                                  onChange={(v) => updateChild(i, "gender", v)}
                                  options={meta.genders || ["Boy", "Girl"]}
                                  error={errors[`child_${i}_gender`]}
                              />

                              <Field
                                  label="Date of birth"
                                  type="date"
                                  value={child.date_of_birth}
                                  onChange={(v) => updateChild(i, "date_of_birth", v)}
                                  error={errors[`child_${i}_date_of_birth`]}
                                  max={new Date().toISOString().slice(0, 10)}
                              />

                              <Field
                                  label="First name"
                                  value={child.first_name}
                                  onChange={(v) => updateChild(i, "first_name", v)}
                                  error={errors[`child_${i}_first_name`]}
                              />

                              <Field
                                  label="Last name"
                                  value={child.last_name}
                                  onChange={(v) => updateChild(i, "last_name", v)}
                                  error={errors[`child_${i}_last_name`]}
                              />

                              <Field
                                  label="Parent/legal guardian first name"
                                  value={child.guardian_first_name}
                                  onChange={(v) => updateChild(i, "guardian_first_name", v)}
                                  error={errors[`child_${i}_guardian_first_name`]}
                              />

                              <Field
                                  label="Parent/legal guardian surname"
                                  value={child.guardian_last_name}
                                  onChange={(v) => updateChild(i, "guardian_last_name", v)}
                                  error={errors[`child_${i}_guardian_last_name`]}
                              />

                              <div className="md:col-span-2">
                                  <Field
                                      label="Guardian HKID or passport number"
                                      value={child.guardian_identity_document}
                                      onChange={(v) =>
                                          updateChild(i, "guardian_identity_document", v)
                                      }
                                      error={errors[`child_${i}_guardian_identity_document`]}
                                  />
                              </div>
                          </div>
                      </section>
                  ))}
        </CardContent>
          </Card>

          <Card>
              <CardHeader>
                  <CardTitle>Applicant Declaration</CardTitle>
                  <CardDescription>
                      Please read and confirm the declaration before submitting your application.
                  </CardDescription>
              </CardHeader>

              <CardContent>
                  <SimpleYes
                      question="I understand this is an application for Travel insurance through a third-party insurance broker. My cover only starts once the application is processed, accepted, and the premium is paid. I’ll receive written confirmation when the policy is active."
                      value={understand}
                      onChange={setUnderstand}
                      error={errors.understand}
                      testid="q-understand"
                  />
              </CardContent>
          </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting || loadingPrice}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Submit insurance application
        </Button>
      </div>
    </form>}
  </div>;
}

function SimpleYes({ question, value, onChange, error, testid }) {
    return (
        <div data-error={error ? "true" : undefined}>
            <div className="text-sm text-navy-700">{question}</div>
            <div className="flex gap-6 mt-3">
                {["yes", "no"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm text-navy-700">
                        <input
                            type="radio"
                            name={testid}
                            checked={value === opt}
                            onChange={() => onChange(opt)}
                            data-testid={`${testid}-${opt}`}
                            className="h-4 w-4 accent-navy-700"
                        />
                        {opt === "yes" ? "Yes" : "No"}
                    </label>
                ))}
            </div>
            {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
        </div>
    );
}

function PriceCard({ label, value, currency, testid, highlight, tone }) {
    const card = highlight
        ? "bg-gradient-to-br from-navy-700 to-navy-800 text-white border-navy-700"
        : tone === "emerald"
            ? "bg-emerald-50 border-emerald-200 text-navy-800"
            : "bg-white border-navy-100/60 text-navy-700";
    return (
        <div
            className={`rounded-2xl border shadow-soft p-5 ${card}`}
            data-testid={testid}
        >
            <div
                className={`text-[10px] uppercase tracking-[0.25em] ${highlight ? "text-gold-500" : "text-navy-500"
                    }`}
            >
                {label}
            </div>
            <div className="font-display text-3xl mt-2">
                <span className="text-sm align-top mr-1">{currency}</span>
                {money(value)}
            </div>
        </div>
    );
}
